import React, { Component } from 'react';
import { FormPanel } from 'shared/wizard/with-discovery-wizard-step-panel';
import WarrantPanel, { WarrantPanel as WarrantPanelClass } from 'xcipio/warrants/forms/warrant-panel';
import DevicePanel, { DevicePanel as DevicePanelClass } from 'xcipio/devices';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import './warrant-and-devices.scss';
import i18n from 'i18n';

export type WarrantAndDevicePanelProps = {
  mode: string
  onDevicesReady?: () => void
  warrantId?: string | null
  onDeleteCase?: (caseId: string)=> void
  onAddDevice?: () => void
  onDeleteDevice?: () => void
}

export default class WarrantAndDevicePanel extends Component<WarrantAndDevicePanelProps> implements FormPanel {
  warrantPanelRef: React.RefObject<WarrantPanelClass>
  devicePanelRef: React.RefObject<DevicePanelClass>

  constructor(props: WarrantAndDevicePanelProps) {
    super(props);
    this.warrantPanelRef = React.createRef<WarrantPanelClass>();
    this.devicePanelRef = React.createRef<DevicePanelClass>();
  }

  isValid(errors: string[]): boolean {
    const isWarrantPanelValid = this.warrantPanelRef.current?.isValid(errors) ?? false;
    const isDevicePanelValid = this.devicePanelRef.current?.isValid(errors) ?? false;

    return isWarrantPanelValid && isDevicePanelValid;
  }

  isWarrantAndCasesValid(errors: string[]): [boolean, number | null] {
    return this.warrantPanelRef.current?.isWarrantAndCasesValid(errors) ?? [false, null];
  }

  async submit(force = false): Promise<boolean> {
    await this.warrantPanelRef.current?.submit();
    await this.devicePanelRef.current?.submit(force);

    return true;
  }

  hasAnyDeviceChange(): boolean {
    return this.devicePanelRef.current?.hasAnyChange() ?? false;
  }

  hasConfiguredDevices = (): boolean => {
    return this.devicePanelRef.current?.hasConfiguredDevices() ?? false;
  }

  renderInstructionalText(): JSX.Element {
    const { mode } = this.props;

    if (mode === CONFIG_FORM_MODE.Edit) {
      return (
        <p className="instruction-text">
          {i18n.t('warrant.edit.instruction')}
        </p>
      );
    }

    if (mode === CONFIG_FORM_MODE.View) {
      return (
        <p className="instruction-text">
          {i18n.t('warrant.view.instruction')}
        </p>
      );
    }

    return (
      <p className="instruction-text">
        {i18n.t('warrant.create.instruction')}
      </p>
    );
  }

  renderEditNoteText(): JSX.Element | null {
    const { mode } = this.props;

    if (mode === CONFIG_FORM_MODE.Edit) {
      return (
        <p className="instruction-text">
          NOTE: You can change the LEA as long as no changes are made in the Devices.
        </p>
      );
    }
    return null;
  }

  render(): JSX.Element {
    const {
      mode,
      warrantId,
      onDevicesReady,
      onAddDevice,
      onDeleteDevice,
    } = this.props;

    return (
      <div className="warrant-and-devices">
        <h3>{i18n.t('warrant.warrantSection.title')}</h3>
        <WarrantPanel
          mode={mode}
          warrantId={warrantId}
          hasConfiguredDevices={this.hasConfiguredDevices}
          ref={this.warrantPanelRef}
        />
        {this.renderEditNoteText()}
        <h3>{i18n.t('warrant.deviceSection.title')}</h3>
        {this.renderInstructionalText()}
        <DevicePanel
          ref={this.devicePanelRef}
          mode={mode}
          onDevicesReady={onDevicesReady}
          onAddDevice={onAddDevice}
          onDeleteDevice={onDeleteDevice}
        />
      </div>
    );
  }
}
